import React, { useEffect, useState } from 'react';
import { 
  Search, Loader2, X, Gift, Users as UsersIcon, 
  Copy, Plus, CheckCircle, AlertCircle, TrendingUp, 
  Edit, Trash2 
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { parsePositiveInt, validateReferralCode } from '../../utils/inputValidation';

const ReferralsSection = () => {
  const [promoters, setPromoters] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal de detalles
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  // Modal para asignar/editar código
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // 🔥 Nuevo estado
  const [editingPromoter, setEditingPromoter] = useState(null); // 🔥 Nuevo estado
  
  const [usersWithoutCode, setUsersWithoutCode] = useState([]);
  const [selectedUserToAssign, setSelectedUserToAssign] = useState('');
  const [newCodeInput, setNewCodeInput] = useState('');

  useEffect(() => {
    fetchPromoters();
  }, []);

  const fetchPromoters = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, phone, referral_code, created_at')
      .not('referral_code', 'is', null)
      .order('id', { ascending: false });

    if (!error && data) {
      setPromoters(data);
      setFiltered(data);
    }
    setLoading(false);
  };

  const fetchUsersWithoutCode = async () => {
    const { data } = await supabase
      .from('users')
      .select('id, first_name, last_name, phone')
      .is('referral_code', null);
    setUsersWithoutCode(data || []);
  };

  const handleSearch = (text) => {
    setSearch(text);
    if (!text.trim()) {
      setFiltered(promoters);
      return;
    }
    const filteredResults = promoters.filter(p =>
      (p.first_name + ' ' + p.last_name).toLowerCase().includes(text.toLowerCase()) ||
      p.referral_code.toLowerCase().includes(text.toLowerCase())
    );
    setFiltered(filteredResults);
  };

  const openModal = async (promoter) => {
    setSelectedPromoter(promoter);
    setLoadingRefs(true);
    const { data, error } = await supabase
      .from('referrals')
      .select(`
        created_at,
        first_transaction_done,
        referred:referred_id ( id, first_name, last_name, phone )
      `)
      .eq('referrer_id', promoter.id)
      .order('created_at', { ascending: false });

    if (!error && data) setReferrals(data);
    setLoadingRefs(false);
  };

  const handleAssignCode = async () => {
    if (!selectedUserToAssign || !newCodeInput.trim()) return alert('Selecciona un usuario y escribe un código');
    const cleanCode = validateReferralCode(newCodeInput);

    const { error } = await supabase.rpc('admin_set_referral_code', {
      p_user_id: parsePositiveInt(selectedUserToAssign, 'ID de usuario'),
      p_code: cleanCode
    });

    if (error) {
      if (error.code === '23505') alert('Este código ya está en uso.');
      else alert('Error al asignar código');
      return;
    }

    setShowAssignModal(false);
    setSelectedUserToAssign('');
    setNewCodeInput('');
    fetchPromoters();
  };

  // 🔥 NUEVA FUNCIÓN: Actualizar código existente
  const handleUpdateCode = async () => {
    if (!newCodeInput.trim()) return alert('Escribe el nuevo código');
    const cleanCode = validateReferralCode(newCodeInput);

    const { error } = await supabase.rpc('admin_set_referral_code', {
      p_user_id: parsePositiveInt(editingPromoter.id, 'ID de usuario'),
      p_code: cleanCode
    });

    if (error) {
      if (error.code === '23505') alert('Este código ya está en uso.');
      else alert('Error al actualizar código');
      return;
    }

    setShowEditModal(false);
    setEditingPromoter(null);
    setNewCodeInput('');
    fetchPromoters();
  };

  // 🔥 NUEVA FUNCIÓN: Eliminar promotor (quitar código)
  const handleDeletePromoter = async (e, promoter) => {
    e.stopPropagation(); // Evitar que abra el modal de detalles
    const confirmDelete = window.confirm(`¿Estás seguro de eliminar a ${promoter.first_name} como promotor? Su código "${promoter.referral_code}" dejará de ser válido.`);
    
    if (!confirmDelete) return;

    const { error } = await supabase.rpc('admin_set_referral_code', {
      p_user_id: parsePositiveInt(promoter.id, 'ID de usuario'),
      p_code: ''
    });

    if (error) {
      alert('Error al eliminar promotor');
    } else {
      fetchPromoters();
    }
  };

  const totalInvitados = referrals.length;
  const metasCumplidas = referrals.filter(r => r.first_transaction_done).length;

  return (
    <div className="animate-in fade-in duration-500">
      
      {/* HEADER Y BUSCADOR */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
            <Gift className="w-6 h-6 text-indigo-600" /> Promotores y Referidos
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1">Supervisa quién trae más usuarios a la app</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-2xl shadow-sm border border-slate-100 w-full md:w-72 focus-within:ring-4 focus-within:ring-indigo-500/10">
            <Search className="w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar promotor o código..."
              className="flex-1 outline-none text-sm font-medium text-slate-700 bg-transparent"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <button 
            onClick={() => { fetchUsersWithoutCode(); setShowAssignModal(true); }}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-2xl font-bold transition-all shadow-sm active:scale-95 whitespace-nowrap"
          >
            <Plus className="w-5 h-5" /> Nuevo Promotor
          </button>
        </div>
      </div>

      {/* TABLA DE PROMOTORES */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[32px] border border-slate-100 shadow-sm">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
          <p className="text-slate-500 font-bold">Cargando promotores...</p>
        </div>
      ) : (
        <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest border-b bg-slate-50/50">
                  <th className="py-5 pl-8">Promotor</th>
                  <th className="py-5">Código</th>
                  <th className="py-5">Contacto</th>
                  <th className="py-5">Fecha Alta</th>
                  <th className="py-5 pr-8 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map(promoter => (
                  <tr key={promoter.id} className="group hover:bg-indigo-50/30 transition-colors cursor-pointer" onClick={() => openModal(promoter)}>
                    <td className="py-4 pl-8">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-black text-sm shrink-0">
                          {promoter.first_name?.charAt(0)}{promoter.last_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 capitalize leading-tight">{promoter.first_name} {promoter.last_name}</p>
                          <p className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {promoter.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 font-mono font-bold text-sm rounded-lg border border-indigo-100">
                        {promoter.referral_code}
                      </span>
                    </td>
                    <td className="py-4 font-mono text-sm text-slate-600">{promoter.phone}</td>
                    <td className="py-4 text-xs font-medium text-slate-500">{new Date(promoter.created_at).toLocaleDateString()}</td>
                    <td className="py-4 pr-8 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setEditingPromoter(promoter); setNewCodeInput(promoter.referral_code); setShowEditModal(true); }}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Editar código"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={(e) => handleDeletePromoter(e, promoter)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar promotor"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button className="ml-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest bg-slate-100 text-slate-600 px-4 py-2 rounded-xl group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                          <UsersIcon className="w-4 h-4" /> Ver Referidos
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================== MODAL: VER REFERIDOS DE UN PROMOTOR ================== */}
      <AnimatePresence>
        {selectedPromoter && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-6 md:p-8 bg-gradient-to-br from-indigo-600 to-purple-800 text-white relative shrink-0">
                <button onClick={() => setSelectedPromoter(null)} className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"><X className="w-5 h-5" /></button>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-[20px] bg-white text-indigo-600 flex items-center justify-center font-black text-2xl shadow-lg">
                    {selectedPromoter.first_name?.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-black tracking-tight capitalize">{selectedPromoter.first_name} {selectedPromoter.last_name}</h2>
                    <p className="text-indigo-200 font-mono text-sm mt-1">Código actual: <span className="text-white font-bold">{selectedPromoter.referral_code}</span></p>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto bg-slate-50 p-6 md:p-8 flex flex-col custom-scrollbar">
                <div className="grid grid-cols-2 gap-4 mb-6 shrink-0">
                  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Total Invitados</p>
                      <p className="text-3xl font-black text-slate-800">{loadingRefs ? '-' : totalInvitados}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center"><UsersIcon className="w-5 h-5 text-indigo-600" /></div>
                  </div>
                  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Metas Cumplidas</p>
                      <p className="text-3xl font-black text-green-600">{loadingRefs ? '-' : metasCumplidas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-green-600" /></div>
                  </div>
                </div>

                <h3 className="font-black text-slate-800 text-sm uppercase tracking-widest flex items-center gap-2 mb-4 shrink-0">Historial de usuarios</h3>
                {loadingRefs ? (
                  <div className="flex justify-center py-10 flex-1"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
                ) : referrals.length > 0 ? (
                  <div className="space-y-3">
                    {referrals.map((ref, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm">
                        <div>
                          <p className="font-bold text-slate-800 capitalize text-sm">{ref.referred?.first_name} {ref.referred?.last_name}</p>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1">{new Date(ref.created_at).toLocaleDateString()} • {ref.referred?.phone}</p>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${ref.first_transaction_done ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {ref.first_transaction_done ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {ref.first_transaction_done ? 'Envió' : 'Pendiente'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-200 flex-1 flex flex-col justify-center">
                    <p className="text-slate-500 font-bold">Sin referidos registrados.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ================== MODAL: ASIGNAR NUEVO CÓDIGO ================== */}
      <AnimatePresence>
        {showAssignModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-[32px] shadow-2xl p-6 md:p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-black text-xl text-slate-800">Nuevo Promotor</h3>
                <button onClick={() => { setShowAssignModal(false); setNewCodeInput(''); }} className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">Seleccionar Usuario</label>
                  <select value={selectedUserToAssign} onChange={e => setSelectedUserToAssign(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-xl text-sm font-medium outline-none focus:border-indigo-500"
                  >
                    <option value="">-- Elige un usuario --</option>
                    {usersWithoutCode.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.phone})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">Código Único</label>
                  <input type="text" value={newCodeInput} onChange={e => setNewCodeInput(e.target.value.toUpperCase())}
                    placeholder="Ej: REY2025" className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-xl font-mono text-center font-bold tracking-widest outline-none focus:border-indigo-500 uppercase"
                  />
                </div>
                <button onClick={handleAssignCode} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 rounded-xl mt-4 transition-colors">Guardar y Asignar</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ================== MODAL: EDITAR CÓDIGO EXISTENTE ================== */}
      <AnimatePresence>
        {showEditModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-[32px] shadow-2xl p-6 md:p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-black text-xl text-slate-800">Editar Código</h3>
                  <p className="text-xs text-slate-400 font-bold uppercase mt-1">{editingPromoter?.first_name} {editingPromoter?.last_name}</p>
                </div>
                <button onClick={() => { setShowEditModal(false); setEditingPromoter(null); setNewCodeInput(''); }} className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block">Nuevo Código</label>
                  <input type="text" value={newCodeInput} onChange={e => setNewCodeInput(e.target.value.toUpperCase())}
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-xl font-mono text-center font-bold tracking-widest outline-none focus:border-indigo-500 uppercase text-indigo-600"
                  />
                </div>
                <button onClick={handleUpdateCode} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 rounded-xl mt-4 transition-colors">Actualizar Código</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ReferralsSection;

